import { css } from '@styled-system/css';
import { type FC } from 'react';
import Skeleton from 'react-loading-skeleton';

export interface DoubleStatCardProps {
  title: string;
  stat1: { value: string; label: string; icon: string; isHighlight?: boolean };
  stat2: { value: string; label: string; icon: string };
  loading?: boolean;
}

export const DoubleStatCard: FC<DoubleStatCardProps> = ({ title, stat1, stat2, loading }) => {
  const cardStyles = css({
    bg: 'base.segBg', // #16171b
    border: '0.75px solid',
    borderColor: 'base.border', // #1e1f24
    borderRadius: 'lg', // 8px
    height: '13.75rem', // 220px
    width: '15.625rem', // 250px
    display: 'flex',
    flexDirection: 'column',
  });

  const headerStyles = css({
    height: '1.75rem', // 28px
    px: '0.81rem',
    py: '0.5rem',
    position: 'relative',
    borderBottomColor: 'base.border',
    borderBottomWidth: '1px',
  });

  const titleStyles = css({
    fontSize: 'sm',
    lineHeight: '1.07',
    fontWeight: 'medium',
    fontFamily: 'Manrope, sans-serif',
    color: 'font.lowkey', // #4f5055
  });

  const contentStyles = css({
    flex: 1,
    px: '1.3125rem', // 21px
    py: '1.375rem', // 22px
    display: 'flex',
    flexDirection: 'column',
    gap: '2.625rem', // 42px
  });

  const statStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem', // 6px
  });

  const valueStyles = (isHighlight?: boolean) =>
    css({
      fontSize: '1rem', // 16px
      fontWeight: 'bold',
      fontFamily: 'Manrope, sans-serif',
      color: isHighlight ? 'accent.primary' : 'font.primary', // #5e69d3 ou #e6e6e9
      lineHeight: '1.07',
    });

  const labelStyles = css({
    fontSize: 'sm', // 11px
    fontWeight: 'medium',
    fontFamily: 'Manrope, sans-serif',
    color: 'font.lowkey', // #4f5055
    lineHeight: '1.07',
  });

  return (
    <div className={cardStyles}>
      <div className={headerStyles}>
        <div className={titleStyles}>{title}</div>
      </div>
      <div className={contentStyles}>
        <div className={statStyles}>
          <div className={valueStyles(stat1.isHighlight)}>
            {loading ? <Skeleton width="3.75rem" height="1.0625rem" /> : stat1.value}
          </div>
          <div className={labelStyles}>
            {stat1.icon} {stat1.label}
          </div>
        </div>
        <div className={statStyles}>
          <div className={valueStyles()}>{loading ? <Skeleton width="3.75rem" height="1.0625rem" /> : stat2.value}</div>
          <div className={labelStyles}>
            {stat2.icon} {stat2.label}
          </div>
        </div>
      </div>
    </div>
  );
};
